import {
  Box,
  Button,
  Card,
  DiscountBadge,
  Heading,
  IconLabel,
  PriceButton,
  ProductPrice,
  Stack,
  Text,
  Tooltip,
} from "@vinomofo/components";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { GlassIcon, LocationPinIcon, VarietalIcon } from "@vinomofo/icons";
import { Tiers, VinoUser } from "../../contexts/auth/state";

import { AfterpayPlacement } from "../afterpay-placement";
import { BellToggleV2 } from "../bell-togglev2";
import { COLORS } from "../../styles";
import { CountdownTimer } from "../offer-card/CountdownTimer";
import { InsertData } from "../../../api/src/services/dynamodb";
import { OfferCtaMessage } from "../offer-cta-message";
import { PayLaterWrapper } from "../pay-later-wrapper";
import { PayPal4Placement } from "../paypal-pay-4-placement";
import { PriceDrop } from "../price-drop";
import { RemindMe } from "../remind-me";
import { StickyAddToCart } from "../../components/sticky-add-to-cart";
import { UrgencyMessage } from "../../types";
import { VinoDataOffer } from "../../types/vino-data-types";
import WineGlassIcon from "./WineGlassIcon";
import { colorMap } from "../wine-notes/colorMap";
import config from "../../../config.json";
import { determineTierIndex } from "../../utils/loyalty";
import { discountDisplays } from "../../utils/constants";
import isBrowser from "../../utils/is-browser";
import mapSlugGroup from "../../utils/map-slug-group";
import { navigate } from "gatsby";
import { priceDescriptionFromVinoDataOffer } from "../../transformers/vino-data/price-description";
import styled from "styled-components";
import useAuth from "../../hooks/use-auth";
import { useCart } from "../../hooks/use-cart";
import { useLoyalty } from "../../hooks/account/use-loyalty";
import useOneClickCheckout from "../../hooks/use-one-click-checkout";
import { useResponsiveValue } from "../../hooks/use-match-media";
import useScreenSize from "../../hooks/use-screen-size";
import { isFlagEnabled } from "../../utils/is-flag-enabled";

export interface OfferHeroCardProps {
  productId: string;
  endsAt?: Date;
  price: number;
  priceDescription?: string;
  region: string;
  style: string;
  rrp?: number;
  shortDescription: string;
  hideStickyTop?: boolean;
  showCountdown: boolean;
  showPriceDrop: boolean;
  showPaymentOptions?: boolean;
  hideIconStack?: boolean;
  urgencyMessage?: UrgencyMessage;
  isSoldOut?: boolean;
  discountPercentage?: number;
  discountDollarValue: number;
  discountDisplay: string;
  showDiscountBadge?: boolean;
  backgroundColor?: string;
  name?: string;
  offer: VinoDataOffer;
  disableAllDisplayFlags?: boolean;
  hideIconVarietal?: boolean;
  isMarquee?: boolean;
}

const ButtonStyled = styled(Button)`
  &.btn-is-loading {
    color: rgba(255, 255, 255, 0);
    background-color: #48a90f;
    border-color: #48a90f;
    transition: all 0.5s;
    &:hover {
      color: transparent;
    }
    &::before {
      content: "Loading ...";
      color: rgba(255, 255, 255, 0.7);
      position: absolute;
      left: 0;
      right: 0;
      animation: moveUp 0.5s;
    }
  }
`;

const StyledCard = styled(Card)`
  @media ${({ theme }) => theme.mq.max.mobile} {
    box-shadow: none;
  }
`;

const ExtendedPriceButton = styled(Box)`
  display: none;
  @media ${({ theme }) => theme.mq.max.mobile} {
    display: block;
  }
`;

const StylePriceButtonBox = styled(Box)`
  position: relative;
  button {
    font-size: 16px;
  }
  @media ${({ theme }) => theme.mq.max.tablet} {
    max-width: 320px;
    margin: 0 auto;
  }
`;

const IconStack = styled(Stack)`
  @media ${({ theme }) => theme.mq.max.tablet} {
    text-align: center;
    justify-content: center;
  }
`;

const StyledPriceBox = styled(Box)`
  @media ${({ theme }) => theme.mq.max.tablet} {
    flex-direction: column;
  }
`;

const StyledHeading = styled(Heading)`
  @media ${({ theme }) => theme.mq.max.tablet} {
    text-align: center;
  }
`;

const StyledShortDescriptionText = styled(Text)`
  @media ${({ theme }) => theme.mq.max.tablet} {
    text-align: center;
  }
`;

const StyledPriceText = styled(Text)`
  @media ${({ theme }) => theme.mq.max.tablet} {
    padding-bottom: 20px;
  }
`;

const ExtendedHeading = styled(Stack)`
  @media ${({ theme }) => theme.mq.max.mobile} {
    display: flex;
    /* flex-direction: column-reverse; */
    text-align: center;
    > div:first-of-type {
      margin-top: 10px;
    }
  }
`;

const StyledCardStack = styled(Stack)`
  @media ${({ theme }) => theme.mq.max.mobile} {
    margin-top: 0px;
    padding-top: 0px;
    > div:last-of-type {
      display: none;
    }
    div {
      div {
        justify-content: center;
        text-align: center;
      }
    }
  }

  > div:has(.shortDesc) {
    margin-top: 0;
  }
`;

const StyledDiscountBadge = styled(DiscountBadge)`
  height: 80px;
  width: 80px;
  margin-top: -20px;
  > span:first-of-type {
    font-size: 30px;
    font-weight: 900;
  }
  > p:first-of-type {
    font-size: 14px;
    font-weight: 700;
  }
`;

const StyledBoxWrapper = styled(Box)`
  margin-top: 12px;
  > div {
    flex: 1;
    justify-content: center;
  }
`;

export const OfferHeroCard = ({
  productId,
  endsAt,
  price,
  priceDescription,
  region,
  style,
  rrp,
  shortDescription,
  showCountdown,
  showPriceDrop,
  hideStickyTop,
  urgencyMessage,
  discountPercentage,
  discountDollarValue,
  discountDisplay,
  showDiscountBadge = false,
  isSoldOut = false,
  backgroundColor,
  name,
  offer,
  hideIconStack = false,
  showPaymentOptions = true,
  hideIconVarietal = false,
  disableAllDisplayFlags = false,
  isMarquee = false,
  ...rest
}: OfferHeroCardProps): JSX.Element => {
  const isAU = process.env.GATSBY_VM_MARKET === "AU";
  const { addItem, isLoading, cart } = useCart();
  const {
    isProcessing,
    isBuying,
    isRedirecting: oneClickCheckoutRedirecting,
  } = useOneClickCheckout();
  const {
    user,
    addToFavourites,
    removeToFavourites,
    favourites,
    fetchingFavourites,
  } = useAuth();
  const scrollTriggerElement = useRef<HTMLDivElement>();
  const [productLoading, setProductLoading] = useState({});
  const [scrollAmount, setScrollAmount] = useState(0);
  const isPreselling = offer?.inventoryStatus === "PRESELLING";
  const userAppMeta = user?.["https://www.vinomofo.com/app_metadata"];
  const customerId =
    userAppMeta?.[`BIGCOMMERCE_${process.env.GATSBY_VM_MARKET}_CUSTOMER_ID`];
  const market = process.env.GATSBY_VM_MARKET || "AU";
  const hideRRP = offer?.displayFlags?.includes("HIDE_RRP");
  const [isTierEligible, setIsTierEligible] = useState(false);
  const { loyalty, showLoyalty } = useLoyalty();
  const { isMobile } = useScreenSize();

  const favouriteUserId_local = isBrowser()
    ? localStorage.getItem("favouriteId")
    : null;

  const favouriteUserId = useMemo(() => {
    return user?.email ? user.email : favouriteUserId_local;
  }, [user?.email, favouriteUserId_local]);

  const favouriteKey = useMemo(() => {
    return `${favouriteUserId}_KEY_${offer?.objectID || offer?.id}`;
  }, [favouriteUserId, offer, favourites]);

  const [isInFavourites, setIsInFavourites] = useState(false);

  useEffect(() => {
    if (favourites !== undefined && !fetchingFavourites) {
      const ticked = favourites?.some((item) => item.id === favouriteKey);
      setIsInFavourites(ticked);
    }
  }, [favourites, fetchingFavourites, favouriteKey, favouriteUserId]);

  const handleSubscription = async (id?: string) => {
    await addToFavourites({
      user_id: id || favouriteUserId,
      offerJson: offer,
      wine_name: offer?.wines?.[0].name || "no-name",
      sku_code: offer.skuCode || "no-code",
      offer_name: offer?.name || "no-name",
      product_id: offer.objectID || offer.id,
    } as InsertData);
  };

  const handleUnsubscription = () => {
    if (isInFavourites && favouriteKey) removeToFavourites(favouriteKey);
  };

  useEffect(() => {
    if (user) {
      const { tier } = determineTierIndex(
        user as VinoUser,
        showLoyalty,
        loyalty?.loyalty_tier
      );

      const includesUserTier = offer?.tiersEligible?.includes(
        tier?.toUpperCase() as Tiers
      );
      const includesALLTier = offer?.tiersEligible?.includes("ALL" as Tiers);
      const hideVimExclusive = isFlagEnabled(
        offer?.displayFlags || [],
        "HIDE_VIM_EXCLUSIVE"
      );
      setIsTierEligible(
        includesUserTier && !includesALLTier && !hideVimExclusive
      );
    }
  }, [loyalty, offer, user]);

  const handleAddButtonClick = (e) => {
    e.stopPropagation();

    if (!isSoldOut) {
      addItem(productId, offer, customerId, "Details Card");
      setProductLoading((oldState) => ({ ...oldState, [productId]: true }));
    } else {
      navigate(offer?.route?.path || "/");
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", scrollHandler);
    return () => window.removeEventListener("scroll", scrollHandler);
  }, []);

  const btnPosition = scrollTriggerElement?.current?.getElementsByTagName(
    "button"
  )?.[0]?.offsetTop;

  const triggerPoint =
    btnPosition + scrollTriggerElement?.current?.offsetTop + 100;

  const scrollHandler = () => {
    setScrollAmount(
      window.pageYOffset ||
        (document.documentElement || document.body).scrollTop
    );
  };

  useEffect(() => {
    if (productLoading[productId] && !isLoading)
      setProductLoading((oldState) => ({ ...oldState, [productId]: false }));
  }, [isLoading, productLoading, productId]);

  const btnLoading = isLoading && productLoading[productId];
  const isDisabled = isBuying || isProcessing || oneClickCheckoutRedirecting;

  const ALLOW_ONE_CLICK_CHECKOUT =
    process.env.GATSBY_ALLOW_ONE_CLICK_CHECKOUT === "true";

  let priceButtonColor = "white";
  let priceButtonBGColor =
    urgencyMessage && COLORS[urgencyMessage.backgroundColor];
  if (isTierEligible) {
    priceButtonBGColor = COLORS.black;
    priceButtonColor = COLORS.mofoGreen;
  }

  return (
    <Fragment key={favouriteKey}>
      {!hideStickyTop && (
        <StickyAddToCart
          productId={productId}
          name={name}
          price={price}
          rrp={
            offer.sku.skuType === "MultiWineSKU"
              ? offer.sku.packRRP
              : !offer.is_wine
              ? null
              : offer.sku.unitRRP
          }
          priceDescription={
            offer.is_wine ? priceDescriptionFromVinoDataOffer(offer) : ""
          }
          offer={offer}
          show={scrollAmount > triggerPoint}
          isSoldOut={isSoldOut}
        />
      )}
      <StyledCard backgroundColor="white" {...rest}>
        {!disableAllDisplayFlags && urgencyMessage && (
          <OfferCtaMessage
            width="100%"
            borderRadius={["5px", "5px 5px 0 0"]}
            py="3px"
            message={urgencyMessage}
            isTierEligible={isTierEligible}
            display={isTierEligible ? ["none", "none", "block"] : ["block"]}
          />
        )}

        <Box
          padding={backgroundColor ? "30px" : ["10px 0", "30px"]}
          width="100%"
        >
          <StyledCardStack space={3}>
            <ExtendedHeading space={1}>
              {!hideIconStack && (
                <IconStack space={2} horizontal>
                  {style && (
                    <Box>
                      <IconLabel
                        icon={
                          offer?.isSpirit ? <GlassIcon /> : <WineGlassIcon />
                        }
                        label={style}
                      />
                    </Box>
                  )}
                  <Box>
                    {region ? (
                      <IconLabel icon={<LocationPinIcon />} label={region} />
                    ) : (
                      <br />
                    )}
                  </Box>
                </IconStack>
              )}
              {/* {isSoldOut && (
                <Box
                  position="absolute"
                  right="0"
                  width="60px"
                  top={isMarquee ? "15px" : "40px"}
                >
                  <Tooltip
                    direction="top"
                    delay={100}
                    content={
                      !isInFavourites
                        ? "Save this item to your favorites!"
                        : "Remove this from your favorites!"
                    }
                    styles={{
                      width: "120px",
                      padding: "5px",
                      lineHeight: 1.4,
                      fontSize: "11px",
                    }}
                  >
                    <BellToggleV2
                      data-active={isInFavourites}
                      onSubscribe={handleSubscription}
                      onUnsubscribe={handleUnsubscription}
                      active={isInFavourites}
                      user_id={customerId}
                      style={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: "white",
                        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                        padding: "16px",
                        opacity: "1",
                        borderRadius: "50%",
                      }}
                    />
                  </Tooltip>
                </Box>
              )} */}
              <StyledHeading variant="xs">
                <Text
                  as={"h3"}
                  fontFamily='"GT Walsheim", sans-serif'
                  lineHeight="1"
                  color={COLORS.black}
                  fontSize={["18px", "20px", "24px"]}
                >
                  {name}
                </Text>
              </StyledHeading>
              {showCountdown && endsAt && (
                <CountdownTimer
                  end={endsAt}
                  countDays={true}
                  marginTop="20px"
                  height="45px"
                />
              )}
            </ExtendedHeading>

            <ExtendedPriceButton flex="1" ref={scrollTriggerElement}>
              <ProductPrice
                size="lg"
                price={price}
                rrp={(!hideRRP && rrp) || null}
              />
              <Box display="flex" flex="1" alignItems="center" mb={[3, 0, 3]}>
                {priceDescription && (
                  <Text fontSize={[0, 1]}>
                    {offer.is_wine ? priceDescription : ""}
                  </Text>
                )}
                {showPriceDrop && (
                  <PriceDrop
                    flexDirection="row-reverse"
                    marginLeft="8px"
                    paddingTop="5px"
                  />
                )}
              </Box>
              {!isSoldOut ? (
                (!isMobile || hideStickyTop) && (
                  <ButtonStyled
                    disabled={btnLoading || isDisabled}
                    variant="primary"
                    width="100%"
                    onClick={handleAddButtonClick}
                    className={btnLoading ? "btn-is-loading" : ""}
                  >
                    Add to cart
                  </ButtonStyled>
                )
              ) : !offer.isMixed ? (
                <RemindMe
                  customerId={customerId}
                  onSubscribe={handleSubscription}
                  onUnsubscribe={handleUnsubscription}
                  active={isInFavourites}
                  loading={fetchingFavourites}
                />
              ) : (
                <ButtonStyled disabled={true} variant="primary" width="100%">
                  Sorry, Sold Out
                </ButtonStyled>
              )}

              {showPaymentOptions && market.toUpperCase() === "AU" ? (
                <PayLaterWrapper
                  locale="en_AU"
                  currency="AUD"
                  amount={offer.packPrice}
                />
              ) : null}
            </ExtendedPriceButton>

            <StyledShortDescriptionText
              variant="sm"
              color="text.heading"
              className="shortDesc"
            >
              {shortDescription}
            </StyledShortDescriptionText>

            <Box display={["block", "block", "block"]} alignItems="center">
              <StyledPriceBox
                display="flex"
                flex="1"
                alignItems="center"
                mb={[3, 0, 3]}
              >
                {priceDescription && (
                  <StyledPriceText fontSize={[0, 1]}>
                    {offer.is_wine ? priceDescription : ""}
                  </StyledPriceText>
                )}
                {showPriceDrop && (
                  <PriceDrop
                    flexDirection="row-reverse"
                    marginLeft="8px"
                    paddingTop="5px"
                  />
                )}

                <Box
                  height="1px"
                  backgroundColor="divider"
                  flexGrow="1"
                  marginRight={["0", "20px", "0"]}
                  marginLeft="15px"
                />
              </StyledPriceBox>

              <Box flex="1" ref={scrollTriggerElement}>
                {isSoldOut && !isMarquee && !offer.isMixed && (
                  <RemindMe
                    customerId={customerId}
                    onSubscribe={handleSubscription}
                    onUnsubscribe={handleUnsubscription}
                    active={isInFavourites}
                    loading={fetchingFavourites}
                  />
                )}
                <StylePriceButtonBox>
                  <div
                    style={{
                      display: !(isSoldOut && !isMarquee && !offer.isMixed)
                        ? "block"
                        : "none",
                    }}
                  >
                    <PriceButton
                      onClick={handleAddButtonClick}
                      fullPrice={(!hideRRP && rrp) || null}
                      displayPrice={price}
                      cta="Add"
                      size={useResponsiveValue(
                        hideRRP ? ["md", "md", "md"] : ["md", "lg", "lg"]
                      )}
                      isAvailable={!isSoldOut}
                      isLoading={btnLoading}
                      ctaBackgroundColor={priceButtonBGColor}
                      color={priceButtonColor}
                      notifyMeText={
                        !offer.isMixed ? "Notify me when available" : ""
                      }
                    />
                  </div>
                </StylePriceButtonBox>
                {!hideIconVarietal && offer.varietal && (
                  <StyledBoxWrapper
                    display="flex"
                    justifyContent="space-around"
                  >
                    <IconLabel
                      icon={offer.is_wine && <VarietalIcon />}
                      label={offer.is_wine ? offer.varietal : ""}
                      alignSelf="center"
                      fontSize={["8px", "10px"]}
                      color={colorMap[offer.category?.toLowerCase()] || "brand"}
                      height={"10px"}
                    />
                  </StyledBoxWrapper>
                )}

                {showPaymentOptions && market.toUpperCase() === "AU" ? (
                  <PayLaterWrapper
                    locale="en_AU"
                    currency="AUD"
                    amount={offer.packPrice}
                  />
                ) : null}
              </Box>
            </Box>
          </StyledCardStack>
        </Box>

        {showDiscountBadge && (
          <Box
            position="absolute"
            top={[10, -10, -10]}
            right={-10}
            display="flex"
            flexDirection="row"
            justifyContent={"flex-end"}
          >
            <StyledDiscountBadge
              discountDisplay={discountDisplay}
              amount={
                discountDisplay === discountDisplays.dollar
                  ? discountDollarValue
                  : discountPercentage
              }
              variant="lg"
            />
          </Box>
        )}
        {/* {isAU && !isSoldOut && !isPreselling && <DeliveryEstimate />} */}
      </StyledCard>
    </Fragment>
  );
};
